import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Wallet, Landmark } from 'lucide-react';
import { fetchAdAccounts } from '../../redux/metaAdsSlice';
import { formatCurrency } from '../../utils/currency';

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[#D8DDE6]">
        <Icon size={16} className="text-[#0176D3]" />
        <span className="text-[13px] font-bold text-[#2E2E2E]">{title}</span>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );
}

// This used to also show an "Internal Ad Wallet" you could top up via
// Stripe — removed. This CRM doesn't provide any billable service through
// that balance (Meta bills the tenant's own ad account directly for real
// spend), so a separate CRM-tracked balance served no purpose. Only Meta's
// own real account balance is shown here now.
export default function MetaAdsAccountBalancePage() {
  const dispatch = useDispatch();
  const { connected, adAccounts } = useSelector((s) => s.metaAds);

  useEffect(() => { if (connected) dispatch(fetchAdAccounts()); }, [connected, dispatch]);

  if (!connected) {
    return (
      <div className="text-center py-16 border border-dashed border-[#D8DDE6] rounded-[4px]">
        <Wallet size={28} className="mx-auto text-[#D8DDE6] mb-2" />
        <p className="text-[13px] text-[#5C5C5C]">Connect a Meta Ads account in Settings to see your ad account balance.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card title="Meta Account Balances" icon={Landmark}>
        <p className="text-[11px] text-[#5C5C5C] mb-1">
          Read-only mirror of every connected Meta ad account, refreshed daily. Each account is shown in its own currency.
        </p>
        {adAccounts.length === 0 ? (
          <p className="text-[13px] text-[#5C5C5C]">No ad account data yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] font-semibold text-[#706E6B]">
                <tr>
                  <th className="text-left py-2 pr-3">Ad Account</th>
                  <th className="text-right py-2 px-3">Balance</th>
                  <th className="text-right py-2 px-3">Spend Cap</th>
                  <th className="text-right py-2 px-3">Amount Spent</th>
                  <th className="text-left py-2 px-3">Cap Usage</th>
                  <th className="text-left py-2 pl-3">Funding Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D8DDE6]">
                {adAccounts.map((a) => (
                  <tr key={a.id}>
                    <td className="py-2.5 pr-3 font-semibold text-[#2E2E2E]">{a.name} <span className="text-[#5C5C5C] font-normal">({a.currency})</span></td>
                    <td className="py-2.5 px-3 text-right">{a.balance != null ? formatCurrency(a.balance, a.currency) : '—'}</td>
                    <td className="py-2.5 px-3 text-right">{a.spendCap != null ? formatCurrency(a.spendCap, a.currency) : '—'}</td>
                    <td className="py-2.5 px-3 text-right">{a.amountSpent != null ? formatCurrency(a.amountSpent, a.currency) : '—'}</td>
                    <td className="py-2.5 px-3">
                      {a.spendCap > 0 ? (
                        <div className="flex items-center gap-2 w-28">
                          <div className="flex-1 h-1.5 bg-[#F4F6F9] rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${a.amountSpent / a.spendCap >= 0.9 ? 'bg-[#BA0517]' : a.amountSpent / a.spendCap >= 0.7 ? 'bg-[#FE9339]' : 'bg-[#04844B]'}`}
                              style={{ width: `${Math.min(100, (a.amountSpent / a.spendCap) * 100)}%` }}
                            />
                          </div>
                          <span className="text-[11px] text-[#5C5C5C] shrink-0">{Math.round((a.amountSpent / a.spendCap) * 100)}%</span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#5C5C5C]">No cap set</span>
                      )}
                    </td>
                    <td className="py-2.5 pl-3 text-[#5C5C5C]">{a.fundingSource || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {adAccounts[0]?.balanceUpdatedAt && (
          <p className="text-[11px] text-[#5C5C5C]">Last synced {new Date(adAccounts[0].balanceUpdatedAt).toLocaleString()}</p>
        )}
      </Card>
    </div>
  );
}
